import { Routes, Route, Link, useLocation } from 'react-router-dom';
import OrderPage from './components/OrderPage';
import ProductManagement from './components/ProductManagement';
import Orders from './components/Orders';
import OrdersTable from './components/OrdersTable';
import OpeningHoursManagement from './components/OpeningHoursManagement';
import StockManagement from './components/StockManagement';
import CommonOptionsManagement from './components/CommonOptionsManagement';
import KVManagement from './components/KVManagement';
import VisitsManagement from './components/VisitsManagement';
import DiscountManagement from './components/DiscountManagement';
import SumupPage from './components/SumupPage';
import SlotManagement from './components/SlotManagement';
import KitchenView from './components/KitchenView';
import OrderPrint from './components/OrderPrint';
import PaymentTest from './components/PaymentTest';
import AdminLogin from './components/AdminLogin';
import AdminRoute from './components/AdminRoute';
import WhoAreWePage from './components/WhoAreWePage';
import WorkingHoursPage from './components/WorkingHoursPage';
import FollowUsPage from './components/FollowUsPage';
import OurProductsPage from './components/OurProductsPage';
import OurReviewsPage from './components/OurReviewsPage';
import BurgerSafetyBlogPage from './components/BurgerSafetyBlogPage';
import NotifyDriverPage from './components/NotifyDriverPage';
import './App.css';
import { useEnvironment, useTabVisibility } from './hooks';

function App() {
    const location = useLocation();
    const { data: environmentInfo, loading: environmentLoading, error: environmentError } = useEnvironment();

    // Enable tab visibility tracking for headsup notifications
    useTabVisibility();

    const isActive = (path: string) => {
        return location.pathname === path ? 'button active' : 'button';
    };

    // Show admin nav for admin routes, but NOT for kitchen view or order print (standalone pages)
    // Show admin nav for admin routes, but NOT for kitchen view, order print (standalone pages), or the SumUp test callback
    const isAdminRoute = location.pathname.startsWith('/melaba') && 
        location.pathname !== '/melaba/kitchen' && 
        location.pathname !== '/melaba/sumup' &&
        !location.pathname.startsWith('/melaba/order-print');
    const environmentLabel = environmentError
        ? 'unknown'
        : environmentInfo?.environment ?? (environmentLoading ? 'loading…' : undefined);

    return (
        <div className="App">
            {isAdminRoute && (
                <nav className="admin-nav">
                    <div className="nav-brand">
                        <h3>🍽️ Restaurant Admin</h3>
                        {environmentLabel && (
                            <span className="environment-pill" title="Current deployment environment">
                                {environmentLabel}
                            </span>
                        )}
                    </div>
                    <div className="nav-links">
                        <Link to="/melaba" className={isActive('/melaba')}>
                            📦 Products
                        </Link>
                        <Link to="/melaba/stock" className={isActive('/melaba/stock')}>
                            🏪 Stock
                        </Link>
                        <Link to="/melaba/common-options" className={isActive('/melaba/common-options')}>
                            🔗 Common Options
                        </Link>
                        <Link to="/melaba/orders" className={isActive('/melaba/orders')}>
                            📋 Orders
                        </Link>
                        <Link to="/melaba/orders-table" className={isActive('/melaba/orders-table')}>
                            📊 Orders Table
                        </Link>
                        <Link to="/melaba/hours" className={isActive('/melaba/hours')}>
                            🕐 Hours
                        </Link>
                        <Link to="/melaba/kv" className={isActive('/melaba/kv')}>
                            💾 KV Data
                        </Link>
                        <Link to="/melaba/visits" className={isActive('/melaba/visits')}>
                            📊 Visits
                        </Link>
                        <Link to="/melaba/discounts" className={isActive('/melaba/discounts')}>
                            🎟️ Discounts
                        </Link>
                        <Link to="/melaba/slots" className={isActive('/melaba/slots')}>
                            📅 Slots
                        </Link>
                        <a href="/melaba/kitchen" target="_blank" rel="noopener noreferrer" className="button">
                            🍳 Kitchen
                        </a>
                        <Link to="/" className="button secondary">
                            🛍️ View Store
                        </Link>
                    </div>
                </nav>
            )}
            <main>
                <Routes>
                    <Route path="/" element={<OrderPage />} />
                    <Route path="/who-are-we" element={<WhoAreWePage />} />
                    <Route path="/working-hours" element={<WorkingHoursPage />} />
                    <Route path="/our-products" element={<OurProductsPage />} />
                    <Route path="/follow-us" element={<FollowUsPage />} />
                    <Route path="/our-reviews" element={<OurReviewsPage />} />
                    <Route path="/blog/burger-safety" element={<BurgerSafetyBlogPage />} />
                    <Route path="/notify-driver/:orderId" element={<NotifyDriverPage />} />
                    <Route path="/melaba/login" element={<AdminLogin />} />
                    <Route path="/melaba" element={<AdminRoute><ProductManagement /></AdminRoute>} />
                    <Route path="/melaba/stock" element={<AdminRoute><StockManagement /></AdminRoute>} />
                    <Route path="/melaba/common-options" element={<AdminRoute><CommonOptionsManagement /></AdminRoute>} />
                    <Route path="/melaba/orders" element={<AdminRoute><Orders /></AdminRoute>} />
                    <Route path="/melaba/orders-table" element={<AdminRoute><OrdersTable /></AdminRoute>} />
                    <Route path="/melaba/hours" element={<AdminRoute><OpeningHoursManagement /></AdminRoute>} />
                    <Route path="/melaba/kv" element={<AdminRoute><KVManagement /></AdminRoute>} />
                    <Route path="/melaba/visits" element={<AdminRoute><VisitsManagement /></AdminRoute>} />
                    <Route path="/melaba/discounts" element={<AdminRoute><DiscountManagement /></AdminRoute>} />
                    <Route path="/melaba/slots" element={<AdminRoute><SlotManagement /></AdminRoute>} />
                    <Route path="/melaba/kitchen" element={<AdminRoute><KitchenView /></AdminRoute>} />
                    <Route path="/melaba/order-print/:orderId" element={<AdminRoute><OrderPrint /></AdminRoute>} />
                    <Route path="/melaba/sumup" element={<SumupPage />} />
                    <Route path="/payment-test" element={<PaymentTest />} />
                </Routes>
            </main>
        </div>
    );
}

export default App;
